<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useLayout } from './composables/layout';
import { useRoles } from '../../composables/useRoles';
import { useBrand } from '../../composables/useBrand';

import MainMenuItem from './MainMenuItem.vue';

import { menuitems } from '../../config/menu';

const route = useRoute();
const { setActiveMenuItem } = useLayout();
const { isSectionAllowed, isItemAllowed } = useRoles();
const { playMode, fetchBrandDomain } = useBrand();

// Play-mode gating (#2158): items tagged with a `playMode` only show for a brand
// in that mode. Untagged items show for every brand. Play mode arrives with the
// selected brand (hydrated before views load), so gating is correct from the
// first render; real_money remains the default for the unknown case (a store
// persisted before play_mode existed and never re-hydrated).
const activePlayMode = computed(() => playMode.value || 'real_money');

function matchesPlayMode(item) {
    return !item.playMode || item.playMode === activePlayMode.value;
}

// Filter menu items based on group permissions (item-level filtering)
const model = computed(() => {
    return menuitems
        .filter(section => isSectionAllowed(section.label, section.items))
        .map(section => ({
            ...section,
            items: filterItems(section.items)
        }))
        .filter(section => section.items.length > 0);
});

function filterItems(items) {
    if (!items) return [];
    return items
        .filter(item => isItemAllowed(item) && matchesPlayMode(item))
        .map(item => {
            if (item.items) {
                return { ...item, items: filterItems(item.items) };
            }
            return item;
        });
}

// Function to find the active menu item based on current route
function findActiveMenuItem(items, parentKey = '') {
    for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const itemKey = parentKey ? `${parentKey}-${i}` : String(i);

        // Check if this item matches the current route
        if (item.to && (route.path === item.to || route.path.startsWith(item.to + '/'))) {
            return itemKey;
        }

        // Check children if this item has them
        if (item.items && item.items.length > 0) {
            const childResult = findActiveMenuItem(item.items, itemKey);
            if (childResult) {
                return childResult;
            }
        }
    }
    return null;
}

onMounted(() => {
    // Fallback resolve of the play mode for a store that predates play_mode on
    // the selected brand (#2158). Cached + deduped in useBrand; a no-op if
    // already fetched this session.
    fetchBrandDomain();

    // Set the active menu item based on the current route
    const activeKey = findActiveMenuItem(model.value);
    if (activeKey) {
        setActiveMenuItem(activeKey);
    }
});

// Watch for route changes to update active menu item
watch(() => route.path, () => {
    const activeKey = findActiveMenuItem(model.value);
    if (activeKey) {
        setActiveMenuItem(activeKey);
    }
});
</script>

<template>
    <ul class="layout-menu">
        <template v-for="(item, i) in model" :key="item">
            <main-menu-item v-if="!item.separator" :item="item" :index="i"></main-menu-item>
            <li v-if="item.separator" class="menu-separator"></li>
        </template>
    </ul>
</template>
